import { Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import FormWrapper from 'components/molecules/FormWrapper';
import FsluzbyIdentifikace from 'components/molecules/StatniSprava/Identifikace';

import { useDateLimit } from 'hooks/statniSprava/useDateLimit';
import { useFormBasics } from 'hooks/statniSprava/useFormBasics';
import usePageName from 'hooks/usePageName';

import typOsobySelect from 'data/codebooks/specialniSluzby/typOsobySelect';

import isinValidation from 'utils/validations/isinValidation';
import numberValidation from 'utils/validations/numberValidation';
import { rcmaValidationSimple } from 'utils/validations/rcmaValidation';

import AlertComponent from './AlertComponent';
import IsinTable from './IsinTable';
import OdeslaniPozadavkuRows from './odeslaniPozadavkuRows';

interface IF9 {}

const F9: React.FC<IF9> = () => {
    const [form] = Form.useForm();
    const pageName = usePageName();
    const [dateLimits, dateLoading, commonDisabledDate] = useDateLimit();

    const [showIsinTable, setShowIsinTable] = useState<boolean>(false);
    const [
        errMsg,
        currentStep,
        idSluzby,
        upozorneni,
        loading,
        odeslaniResp,
        setErrMsg,
        prev,
        onFormSubmit,
        showPxKlicLoadingMsg
    ] = useFormBasics();
    const [, rerender] = useState<boolean>(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const dataToSend = {
            ...fieldsValue,
            dtvp: fieldsValue.dtvp.format('YYYY-MM-DD'),
            ...(fieldsValue.dtdv && {
                dtdv: fieldsValue.dtdv.format('YYYY-MM-DD')
            }),
            ...(fieldsValue.idmb &&
                typeof fieldsValue.idmb === 'object' && {
                    idmb: fieldsValue.idmb.format('YYYYMMDD')
                }),
            ...(pageName === 'page197' && {
                octg: '1'
            })
        };
        onFormSubmit(dataToSend);
    };

    return (
        // page95b
        <LoadingWrapper loading={loading} loadingText={showPxKlicLoadingMsg ? 'LOADING_PXKLIC' : 'NACITANI'}>
            <Title level={4} text="DUVERNE_INFO_TITLE_F9" />
            {errMsg ? (
                <>
                    <Paragraph text={errMsg} />
                    <Row>
                        <ButtonComponent
                            type="default"
                            buttontext="ZPET"
                            onClick={() => setErrMsg('')}
                            id="f9ZpetBtn"
                        />
                    </Row>
                </>
            ) : (
                <>
                    {currentStep !== 2 ? (
                        <FormWrapper onFinish={onFinish} outForm={form}>
                            {showIsinTable ? (
                                <IsinTable upperForm={form} setShowIsinTable={setShowIsinTable} formIsinName={'cval'} />
                            ) : (
                                <>
                                    <LoadingWrapper loading={dateLoading}>
                                        <DatePickerComponent
                                            name="dtvp"
                                            disabled={currentStep > 0}
                                            label="DUVERNE_INFO_DTVP"
                                            infoTooltipText="DUVERNE_INFO_TOOLTIP_DTVP"
                                            disabledDate={current => commonDisabledDate(current)}
                                            onChange={val => {
                                                val?.format('YYYY-MM-DD') !==
                                                    dayjs().subtract(1, 'days').format('YYYY-MM-DD') &&
                                                    form.setFieldsValue({
                                                        dtdv: undefined
                                                    });

                                                rerender(pv => !pv);
                                            }}
                                            //     defaultValue={dayjs().subtract(1, 'days')}
                                            initialValue={dayjs().subtract(1, 'days')}
                                        />
                                        <DatePickerComponent
                                            name="dtdv"
                                            label="ME_POZADAVKY_DATUM2_DUALNI"
                                            disabled={currentStep > 0}
                                            infoTooltipText="DUVERNE_INFO_TOOLTIP_DTDV"
                                            hide={currentStep === 1 && !form.getFieldValue('dtdv')}
                                            notRequired={true}
                                            disabledDate={current => {
                                                return dateLimits
                                                    ? current.format('YYYY-MM-DD') >=
                                                          form.getFieldValue('dtvp')?.format('YYYY-MM-DD') ||
                                                          current > dayjs(dateLimits.do) ||
                                                          current < dayjs(dateLimits.od)
                                                    : current.format('YYYY-MM-DD') >=
                                                          form.getFieldValue('dtvp')?.format('YYYY-MM-DD');
                                            }}
                                        />
                                    </LoadingWrapper>
                                    {pageName === 'page197' ? (
                                        <>
                                            <InputComponent
                                                disabled={currentStep > 0}
                                                label="ME_POZADAVKY_INDENTIFIKACE_SOUDU"
                                                name="idco"
                                                maxLength={100}
                                            />
                                            <InputComponent
                                                disabled={currentStep > 0}
                                                label="SPISOVA_ZNACKA"
                                                name="actn"
                                                maxLength={30}
                                            />
                                            <FsluzbyIdentifikace
                                                form={form}
                                                disabled={currentStep > 0}
                                                rerender={rerender}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <InputComponent
                                                disabled={currentStep > 0}
                                                label="ME_POZADAVKY_CISLO_JEDNACI"
                                                name="actn"
                                                maxLength={30}
                                            />
                                            <SelectComponent
                                                name="octg"
                                                labelParagraph="ME_POZADAVKY_KATEGORIE_MAJITELE"
                                                labelCol={{
                                                    span: 8
                                                }}
                                                data={typOsobySelect}
                                                onChange={() => {
                                                    form.setFieldsValue({
                                                        identifikace: undefined
                                                    });
                                                    rerender(pv => !pv);
                                                }}
                                                isFormItem={true}
                                                disabled={currentStep > 0}
                                                defaultValue={typOsobySelect[0].value}
                                            />
                                            <FsluzbyIdentifikace
                                                form={form}
                                                disabled={currentStep > 0}
                                                rerender={rerender}
                                            />
                                        </>
                                    )}
                                    <InputComponent
                                        label="ME_POZADAVKY_PREDCISLI_UCTU"
                                        name="cdac"
                                        type="text"
                                        maxLength={4}
                                        rules={numberValidation()}
                                        notRequired={true}
                                        hide={currentStep === 1 && !form.getFieldValue('cdac')}
                                        disabled={currentStep > 0}
                                    />
                                    <InputComponent
                                        label="CISLO_UCTU"
                                        name="rcma"
                                        type="text"
                                        maxLength={12}
                                        rules={rcmaValidationSimple()}
                                        notRequired={true}
                                        hide={currentStep === 1 && !form.getFieldValue('rcma')}
                                        disabled={currentStep > 0}
                                    />
                                    <InputComponent
                                        label="ME_POZADAVKY_DRUH_VYPISU"
                                        name="cval"
                                        type="text"
                                        maxLength={12}
                                        rules={isinValidation()}
                                        capitalize={true}
                                        notRequired={true}
                                        hide={currentStep === 1 && !form.getFieldValue('cval')}
                                        disabled={currentStep > 0}
                                    />
                                    {currentStep < 1 && (
                                        <ButtonComponent
                                            id="f9IsinBtn"
                                            buttontext="DUVERNE_INFO_ISIN_BUTTON"
                                            onClick={() => setShowIsinTable(true)}
                                            style={{
                                                position: 'relative',
                                                top: -12
                                            }}
                                        />
                                    )}
                                    <InputComponent
                                        label={
                                            pageName === 'page95b' || form.getFieldValue('octg') === '2'
                                                ? 'ME_POZADAVKY_PRIJMENO'
                                                : 'PRIJMENI'
                                        }
                                        name="pjma"
                                        type="text"
                                        maxLength={250}
                                        notRequired={
                                            pageName === 'page95b' ||
                                            (form.getFieldValue('octg') === '2' && pageName === 'page625')
                                        }
                                        hide={currentStep === 1 && !form.getFieldValue('pjma')}
                                        disabled={currentStep > 0}
                                    />
                                    <InputComponent
                                        label={
                                            pageName === 'page95b' || form.getFieldValue('octg') === '2'
                                                ? 'ME_POZADAVKY_JMENO'
                                                : 'JMENO'
                                        }
                                        name="jmma"
                                        type="text"
                                        maxLength={250}
                                        notRequired={
                                            pageName === 'page95b' ||
                                            (form.getFieldValue('octg') === '2' && pageName === 'page625')
                                        }
                                        hide={currentStep === 1 && !form.getFieldValue('jmma')}
                                        disabled={currentStep > 0}
                                    />
                                    {currentStep === 0 && !showIsinTable && (
                                        <ButtonComponent
                                            buttontext="ODESLAT"
                                            onClick={() => form.submit()}
                                            id="f9OdeslatBtn"
                                        />
                                    )}
                                    {currentStep !== 0 && upozorneni && (
                                        <AlertComponent
                                            upozorneni={upozorneni}
                                            type={currentStep === 1 ? 'error' : 'info'}
                                        />
                                    )}
                                    {currentStep === 1 && (
                                        <Row className="button-row">
                                            <ButtonComponent
                                                id="f9ZpetAltBtn"
                                                buttontext="ZPET"
                                                onClick={() => prev()}
                                                type="default"
                                            />

                                            <ButtonComponent
                                                buttontext="POTVRDIT"
                                                onClick={() => form.submit()}
                                                id="f9PotvrditBtn"
                                            />
                                        </Row>
                                    )}
                                </>
                            )}
                        </FormWrapper>
                    ) : (
                        <>
                            <OdeslaniPozadavkuRows idSluzby={idSluzby} datum={odeslaniResp?.datum} />
                            {upozorneni && <AlertComponent upozorneni={upozorneni} type={'info'} />}
                        </>
                    )}
                </>
            )}
        </LoadingWrapper>
    );
};

export default F9;
